/**
 * marketSocketSaga.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Bridges the rAF-batched tick stream from marketSocket.js into Redux.
 *
 * One root saga forks several sub-tasks:
 *
 *   - `tickConsumer` listens to onTickBatch from the singleton (one batch
 *     per animation frame). For every consumer that has matching data in
 *     Redux, it merges in the new tick fields and dispatches the SAME
 *     action the REST saga would have dispatched (`updateMarketData`,
 *     `updateMarketWatchListData`, `fetchMarketDataSuccess`). The rest of
 *     the app sees no difference — same Redux state shape.
 *
 *   - `sync*Subscriptions` tasks watch the currently-displayed tokens in
 *     each Redux slice and refcount-subscribe them via the singleton.
 *
 *   The REST poll loops in marketSaga / watchlistSaga / useMarketData are
 *   unchanged in logic — they just skip their fetch while the socket is
 *   connected (see the `isMarketSocketConnected()` guard added there).
 *
 *   Defensive coding notes:
 *     - Every sub-task wraps its body in try/catch so one crashing loop
 *       does NOT cancel the others (which is what happens by default in
 *       Redux-Saga when a forked task throws).
 *     - Every Redux selector result is Array.isArray-checked because some
 *       slices start life as `null`, `{}`, or `undefined`.
 *     - Uses `delay(...)` from redux-saga/effects, not a raw setTimeout
 *       promise — the latter cannot be cancelled by the saga middleware.
 */

import { eventChannel } from 'redux-saga';
import {
    take, fork, select, put, call, all, delay,
} from 'redux-saga/effects';

import {
    connectMarketSocket,
    subscribeTokens,
    unsubscribeTokens,
    onTickBatch,
    tokensFromList,
} from '../../services/socket/marketSocket';

import {
    updateMarketData,
} from '../reducers/marketReducer';
import {
    updateMarketWatchListData,
} from '../reducers/watchlistReducer';
import {
    fetchMarketDataSuccess as ordersMarketDataSuccess,
} from '../reducers/orderReducer';
import {
    fetchMarketDataSuccess as addOrderMarketDataSuccess,
} from '../reducers/addorderReducer';
import {
    fetchMarketDataSuccess as portfolioMarketDataSuccess,
} from '../../actions/portfolioActions';

const SYNC_INTERVAL_MS = 500;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function createTickChannel() {
    return eventChannel((emit) => {
        const off = onTickBatch((batch) => emit(batch));
        return off;
    });
}

function mergeTick(row, tick) {
    if (!row || !tick) return row;

    const ltp = tick.ltp ?? row.ltp;
    const close = tick.close ?? row.close;
    // Compute change from ltp vs previous close when the feed/Node build
    // doesn't send netChange/percentChange directly (older Node only sends
    // ltp/open/high/low/close/depth).
    const haveChange = ltp != null && close != null && Number(close) !== 0;
    const computedNet = haveChange ? Number(ltp) - Number(close) : undefined;
    const computedPct = haveChange ? (computedNet / Number(close)) * 100 : undefined;

    return {
        ...row,
        // tradingSymbol may arrive on the tick (Node attaches it from the Redis
        // token→symbol map). Keep any name already present on the row.
        tradingSymbol: row.tradingSymbol || tick.tradingSymbol,
        ltp,
        high: tick.high ?? row.high,
        low: tick.low ?? row.low,
        open: tick.open ?? row.open,
        close,
        // VALUE column shows avgPrice; fall back to the row's value or ltp so a
        // live price is always shown even when avgPrice isn't in the tick.
        avgPrice: tick.avgPrice || row.avgPrice || ltp,
        tradeVolume: tick.tradeVolume ?? row.tradeVolume,
        netChange: tick.netChange ?? computedNet ?? row.netChange,
        percentChange: tick.percentChange ?? computedPct ?? row.percentChange,
        upperCircuit: tick.upperCircuit ?? row.upperCircuit,
        lowerCircuit: tick.lowerCircuit ?? row.lowerCircuit,
        depth: tick.depth ?? row.depth,
    };
}

function asArray(maybe) {
    return Array.isArray(maybe) ? maybe : [];
}

function diffAndSyncSubscriptions(prevList, nextList) {
    const prevSet = new Set(prevList.map((t) => t.token + ':' + t.exchangeType));
    const nextSet = new Set(nextList.map((t) => t.token + ':' + t.exchangeType));
    const toAdd = nextList.filter((t) => !prevSet.has(t.token + ':' + t.exchangeType));
    const toRemove = prevList.filter((t) => !nextSet.has(t.token + ':' + t.exchangeType));
    if (toAdd.length > 0) subscribeTokens(toAdd);
    if (toRemove.length > 0) unsubscribeTokens(toRemove);
}

// ─── 1. Token subscription tasks (one per consumer) ──────────────────────────

function* syncMarketSubscriptions() {
    let prev = [];
    let lastFingerprint = '';
    while (true) {
        try {
            const market = yield select((s) => s.market || {});
            const exchange = market.activeTab;
            const symbolTokens = asArray(market.symbolTokens);
            const tokenList = symbolTokens
                .map((t) => (t && t.token != null ? String(t.token) : ''))
                .filter(Boolean);
            const next = exchange ? tokensFromList(exchange, tokenList) : [];
            const fingerprint = next
                .map((t) => t.token + ':' + t.exchangeType)
                .sort()
                .join('|');
            if (fingerprint !== lastFingerprint) {
                diffAndSyncSubscriptions(prev, next);
                prev = next;
                lastFingerprint = fingerprint;
            }
        } catch (err) {
            console.error('[marketSocketSaga] syncMarketSubscriptions:', err);
        }
        yield delay(SYNC_INTERVAL_MS);
    }
}

function* syncWatchlistSubscriptions() {
    let prev = [];
    let lastFingerprint = '';
    while (true) {
        try {
            const wl = yield select((s) => s.watchlist || {});
            const activeTab = wl.activeTab;
            const rawList = wl.marketWatchListData;

            // marketWatchListData may legitimately be an array, an object
            // keyed by exchange ({ NFO: [...], MCX: [...] }), null, or
            // undefined depending on which point of the saga lifecycle
            // we're in. Normalise to a flat array of rows.
            let rows = [];
            if (Array.isArray(rawList)) {
                rows = rawList;
            } else if (rawList && typeof rawList === 'object') {
                Object.values(rawList).forEach((v) => {
                    if (Array.isArray(v)) rows.push(...v);
                });
            }

            const tokenList = rows
                .map((row) => {
                    if (!row) return '';
                    return String(row.symbolToken ?? row.token ?? '');
                })
                .filter(Boolean);

            const next = activeTab ? tokensFromList(activeTab, tokenList) : [];
            const fingerprint = next
                .map((t) => t.token + ':' + t.exchangeType)
                .sort()
                .join('|');
            if (fingerprint !== lastFingerprint) {
                diffAndSyncSubscriptions(prev, next);
                prev = next;
                lastFingerprint = fingerprint;
            }
        } catch (err) {
            console.error('[marketSocketSaga] syncWatchlistSubscriptions:', err);
        }
        yield delay(SYNC_INTERVAL_MS);
    }
}

function* syncPortfolioSubscriptions() {
    let prev = [];
    let lastFingerprint = '';
    while (true) {
        try {
            const pf = yield select((s) => s.portfolio || {});

            // Portfolio holds tokens in two places — `exchangeTokens`
            // (a {exchange: [tokenStrings]} map built by the saga) and
            // `fetchData` (the merged market rows). Use whichever has data.
            const grouped = {};

            const fromExchangeTokens = pf.exchangeTokens;
            if (fromExchangeTokens && typeof fromExchangeTokens === 'object') {
                Object.entries(fromExchangeTokens).forEach(([ex, toks]) => {
                    if (!Array.isArray(toks)) return;
                    let mapped = ex;
                    if (mapped === 'EQUITY' || mapped === 'EQUITY(INDEX)') mapped = 'NFO';
                    if (!grouped[mapped]) grouped[mapped] = [];
                    toks.forEach((t) => {
                        const s = String(t);
                        if (s && !grouped[mapped].includes(s)) grouped[mapped].push(s);
                    });
                });
            }

            const rows = asArray(pf.fetchData);
            rows.forEach((row) => {
                if (!row || !row.symbolToken) return;
                let ex = row.exchange;
                if (ex === 'EQUITY' || ex === 'EQUITY(INDEX)') ex = 'NFO';
                if (!ex) return;
                if (!grouped[ex]) grouped[ex] = [];
                const tok = String(row.symbolToken);
                if (!grouped[ex].includes(tok)) grouped[ex].push(tok);
            });

            const next = [];
            Object.entries(grouped).forEach(([ex, toks]) => {
                next.push(...tokensFromList(ex, toks));
            });

            const fingerprint = next
                .map((t) => t.token + ':' + t.exchangeType)
                .sort()
                .join('|');
            if (fingerprint !== lastFingerprint) {
                diffAndSyncSubscriptions(prev, next);
                prev = next;
                lastFingerprint = fingerprint;
            }
        } catch (err) {
            console.error('[marketSocketSaga] syncPortfolioSubscriptions:', err);
        }
        yield delay(SYNC_INTERVAL_MS);
    }
}

function* syncAddOrderSubscriptions() {
    let prev = [];
    let lastFingerprint = '';
    while (true) {
        try {
            const addorder = yield select((s) => s.addorder || {});
            const rows = asArray(addorder.marketData);

            const grouped = {};
            rows.forEach((row) => {
                if (!row || !row.symbolToken) return;
                let ex = row.exchange;
                if (ex === 'EQUITY' || ex === 'EQUITY(INDEX)') ex = 'NFO';
                if (!ex) return;
                if (!grouped[ex]) grouped[ex] = [];
                const tok = String(row.symbolToken);
                if (!grouped[ex].includes(tok)) grouped[ex].push(tok);
            });

            const next = [];
            Object.entries(grouped).forEach(([ex, toks]) => {
                next.push(...tokensFromList(ex, toks));
            });

            const fingerprint = next
                .map((t) => t.token + ':' + t.exchangeType)
                .sort()
                .join('|');
            if (fingerprint !== lastFingerprint) {
                diffAndSyncSubscriptions(prev, next);
                prev = next;
                lastFingerprint = fingerprint;
            }
        } catch (err) {
            console.error('[marketSocketSaga] syncAddOrderSubscriptions:', err);
        }
        yield delay(SYNC_INTERVAL_MS);
    }
}

function* syncOrdersSubscriptions() {
    let prev = [];
    let lastFingerprint = '';
    while (true) {
        try {
            const ordersSlice = yield select((s) => s.orders || {});
            const allMarketData = asArray(ordersSlice.allMarketData);

            const grouped = {};
            allMarketData.forEach((order) => {
                if (!order || !order.token) return;
                let ex = order.exchange;
                if (ex === 'EQUITY' || ex === 'EQUITY(INDEX)') ex = 'NFO';
                if (!ex) return;
                if (!grouped[ex]) grouped[ex] = [];
                const tok = String(order.token);
                if (!grouped[ex].includes(tok)) grouped[ex].push(tok);
            });

            const next = [];
            Object.entries(grouped).forEach(([ex, toks]) => {
                next.push(...tokensFromList(ex, toks));
            });

            const fingerprint = next
                .map((t) => t.token + ':' + t.exchangeType)
                .sort()
                .join('|');
            if (fingerprint !== lastFingerprint) {
                diffAndSyncSubscriptions(prev, next);
                prev = next;
                lastFingerprint = fingerprint;
            }
        } catch (err) {
            console.error('[marketSocketSaga] syncOrdersSubscriptions:', err);
        }
        yield delay(SYNC_INTERVAL_MS);
    }
}

// ─── 2. Tick → Redux dispatcher ──────────────────────────────────────────────

function* tickConsumer() {
    const channel = createTickChannel();
    try {
        while (true) {
            let batch;
            try {
                batch = yield take(channel);
            } catch (err) {
                console.error('[marketSocketSaga] tick channel error:', err);
                continue;
            }
            if (!batch || batch.size === 0) continue;

            try {
                // ── Market ────────────────────────────────────────────
                // Cards are seeded (with names) by marketSaga, so here we only
                // merge live prices into the EXISTING cards, IN PLACE — never
                // re-sort or rebuild the list, otherwise the rows jump around on
                // every tick (the "bad loading" flicker). Order is fixed once at
                // seed time and stays stable.
                const marketState = yield select((s) => s.market || {});
                const displayedCards = asArray(marketState.displayedCards);

                if (displayedCards.length > 0) {
                    let touched = false;
                    const next = displayedCards.map((row) => {
                        if (!row) return row;
                        const tick = batch.get(String(row.symbolToken));
                        if (!tick) return row;
                        touched = true;
                        return mergeTick(row, tick);
                    });
                    if (touched) yield put(updateMarketData(next));
                } else {
                    // Cold-load fallback: no seeded cards yet — build from the
                    // subscribed tokens as ticks arrive so the list still fills.
                    const symbolTokens = asArray(marketState.symbolTokens);
                    if (symbolTokens.length > 0) {
                        let touched = false;
                        const next = [];
                        for (const st of symbolTokens) {
                            if (!st || st.token == null) continue;
                            const token = String(st.token);
                            const tick = batch.get(token);
                            const name = tick && (tick.tradingSymbol || st.symbol || st.script_name);
                            if (tick && name) {
                                next.push({
                                    symbolToken: token,
                                    tradingSymbol: name,
                                    script_id: st.script_id ?? null,
                                    exchange_id: st.exchange_id ?? null,
                                    lots: st.lots ?? null,
                                    ltp: tick.ltp,
                                    high: tick.high,
                                    low: tick.low,
                                    open: tick.open,
                                    close: tick.close,
                                    avgPrice: tick.avgPrice ?? tick.ltp ?? 0,
                                    tradeVolume: tick.tradeVolume ?? 0,
                                    netChange: tick.netChange,
                                    percentChange: tick.percentChange,
                                    upperCircuit: tick.upperCircuit,
                                    lowerCircuit: tick.lowerCircuit,
                                    depth: tick.depth,
                                });
                                touched = true;
                            }
                        }
                        if (touched) yield put(updateMarketData(next));
                    }
                }
            } catch (err) {
                console.error('[marketSocketSaga] market merge failed:', err);
            }

            try {
                // ── WatchList ─────────────────────────────────────────
                const watchlistRows = yield select((s) => s.watchlist && s.watchlist.marketWatchListData);
                if (Array.isArray(watchlistRows) && watchlistRows.length > 0) {
                    let touched = false;
                    const next = watchlistRows.map((row) => {
                        if (!row) return row;
                        const tick = batch.get(String(row.symbolToken));
                        if (!tick) return row;
                        touched = true;
                        return mergeTick(row, tick);
                    });
                    if (touched) yield put(updateMarketWatchListData(next));
                }
            } catch (err) {
                console.error('[marketSocketSaga] watchlist merge failed:', err);
            }

            try {
                // ── Portfolio page ────────────────────────────────────
                const portfolioRows = yield select((s) => s.portfolio && s.portfolio.fetchData);
                if (Array.isArray(portfolioRows) && portfolioRows.length > 0) {
                    let touched = false;
                    const next = portfolioRows.map((row) => {
                        if (!row) return row;
                        const tick = batch.get(String(row.symbolToken));
                        if (!tick) return row;
                        touched = true;
                        return mergeTick(row, tick);
                    });
                    if (touched) yield put(portfolioMarketDataSuccess(next));
                }
            } catch (err) {
                console.error('[marketSocketSaga] portfolio merge failed:', err);
            }

            try {
                // ── AddOrder page ─────────────────────────────────────
                const addOrderRows = yield select((s) => s.addorder && s.addorder.marketData);
                if (Array.isArray(addOrderRows) && addOrderRows.length > 0) {
                    let touched = false;
                    const next = addOrderRows.map((row) => {
                        if (!row) return row;
                        const tick = batch.get(String(row.symbolToken));
                        if (!tick) return row;
                        touched = true;
                        return mergeTick(row, tick);
                    });
                    if (touched) yield put(addOrderMarketDataSuccess(next));
                }
            } catch (err) {
                console.error('[marketSocketSaga] addorder merge failed:', err);
            }

            try {
                // ── Orders (open/limit) ───────────────────────────────
                const allOrders = yield select((s) => s.orders && s.orders.allMarketData);
                if (Array.isArray(allOrders) && allOrders.length > 0) {
                    let touched = false;
                    const next = allOrders.map((order) => {
                        if (!order) return order;
                        const tick = batch.get(String(order.token));
                        if (!tick) return order;
                        touched = true;

                        // Mirror the priceDifference logic from orderSaga
                        // so the open-orders cards keep working identically.
                        let bestMarketPrice = null;
                        let priceDifference = null;
                        if (order.type === 'buy' && tick.depth && tick.depth.buy && tick.depth.buy[0]) {
                            bestMarketPrice = tick.depth.buy[0].price;
                            priceDifference = bestMarketPrice - order.price;
                        } else if (order.type === 'sell' && tick.depth && tick.depth.sell && tick.depth.sell[0]) {
                            bestMarketPrice = tick.depth.sell[0].price;
                            priceDifference = order.price - bestMarketPrice;
                        }

                        const marketData = mergeTick(order.marketData || {}, tick);
                        return { ...order, marketData, priceDifference };
                    });
                    if (touched) yield put(ordersMarketDataSuccess(next));
                }
            } catch (err) {
                console.error('[marketSocketSaga] orders merge failed:', err);
            }
        }
    } finally {
        try { channel.close(); } catch (e) { /* ignore */ }
    }
}

// ─── Root ────────────────────────────────────────────────────────────────────

export default function* marketSocketSaga() {
    // Try to open the socket. If the bridge isn't running this still
    // completes — the singleton just stays in "disconnected" state and
    // every consumer falls back to REST polling automatically.
    try {
        yield call(connectMarketSocket);
    } catch (err) {
        console.warn('[marketSocketSaga] connectMarketSocket failed:', err);
    }

    // Fork all bridging tasks. Each is wrapped in try/catch internally so
    // a transient error never cancels the others.
    yield all([
        fork(tickConsumer),
        fork(syncMarketSubscriptions),
        fork(syncWatchlistSubscriptions),
        fork(syncOrdersSubscriptions),
        fork(syncAddOrderSubscriptions),
        fork(syncPortfolioSubscriptions),
    ]);
}
